/**
 * Base UI Components
 * Foundational building blocks with consistent styling
 */

import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

// ============================================================================
// BUTTON
// ============================================================================

export const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  className,
  ...props
}, ref) => {
  const variants = {
    primary: 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-lg shadow-purple-500/25',
    secondary: 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700',
    ghost: 'bg-transparent hover:bg-zinc-800/50 text-zinc-300',
    danger: 'bg-red-600 hover:bg-red-500 text-white',
    success: 'bg-emerald-600 hover:bg-emerald-500 text-white',
    outline: 'bg-transparent border-2 border-purple-500 text-purple-400 hover:bg-purple-500/10'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-6 py-3 text-base gap-2',
    icon: 'p-2'
  };

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-2 focus:ring-offset-zinc-900',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        'active:scale-[0.98]',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {children && <span className="ml-2">{children}</span>}
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon className="w-4 h-4" />}
          {children}
          {Icon && iconPosition === 'right' && <Icon className="w-4 h-4" />}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

// ============================================================================
// INPUT
// ============================================================================

export const Input = forwardRef(({
  label,
  error,
  icon: Icon,
  className,
  containerClassName,
  ...props
}, ref) => {
  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label className="block text-sm font-medium text-zinc-300">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        )}
        <input
          ref={ref}
          className={cn(
            'w-full bg-zinc-900/50 border border-zinc-700/50 rounded-xl px-4 py-2.5',
            'text-white placeholder:text-zinc-500',
            'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50',
            'transition-all duration-200',
            Icon && 'pl-10',
            error && 'border-red-500/50 focus:ring-red-500/50',
            className
          )}
          {...props}
        />
      </div>
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// ============================================================================
// TEXTAREA
// ============================================================================

export const Textarea = forwardRef(({
  label,
  error,
  className,
  containerClassName,
  ...props
}, ref) => {
  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label className="block text-sm font-medium text-zinc-300">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className={cn(
          'w-full bg-zinc-900/50 border border-zinc-700/50 rounded-xl px-4 py-3',
          'text-white placeholder:text-zinc-500',
          'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50',
          'transition-all duration-200 resize-none',
          'min-h-[100px]',
          error && 'border-red-500/50 focus:ring-red-500/50',
          className
        )}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

// ============================================================================
// SELECT
// ============================================================================

export const Select = forwardRef(({
  label,
  error,
  options = [],
  placeholder = 'Select...',
  className,
  containerClassName,
  ...props
}, ref) => {
  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label className="block text-sm font-medium text-zinc-300">
          {label}
        </label>
      )}
      <select
        ref={ref}
        className={cn(
          'w-full bg-zinc-900/50 border border-zinc-700/50 rounded-xl px-4 py-2.5',
          'text-white',
          'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50',
          'transition-all duration-200',
          'appearance-none cursor-pointer',
          error && 'border-red-500/50 focus:ring-red-500/50',
          className
        )}
        {...props}
      >
        <option value="" className="bg-zinc-900">{placeholder}</option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            className="bg-zinc-900"
          >
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

// ============================================================================
// CARD
// ============================================================================

export const Card = forwardRef(({
  children,
  variant = 'default',
  padding = true,
  hover = false,
  className,
  ...props
}, ref) => {
  const variants = {
    default: 'bg-zinc-900/50 border border-zinc-800/50',
    elevated: 'bg-zinc-900/80 border border-zinc-700/50 shadow-xl shadow-black/20',
    ghost: 'bg-transparent border border-zinc-800/30'
  };

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl backdrop-blur-sm',
        variants[variant],
        padding && 'p-6',
        hover && 'transition-all duration-300 hover:border-zinc-700/70 hover:bg-zinc-900/70',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

// ============================================================================
// BADGE
// ============================================================================

export const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  className,
  ...props
}) => {
  const variants = {
    default: 'bg-zinc-800 text-zinc-300',
    primary: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    success: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    warning: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    danger: 'bg-red-500/20 text-red-400 border border-red-500/30',
    info: 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
  };

  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm'
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
};

// ============================================================================
// AVATAR
// ============================================================================

export const Avatar = ({
  src,
  alt = '',
  fallback,
  size = 'md',
  className,
  ...props
}) => {
  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl'
  };

  const initials = fallback || alt?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      className={cn(
        'relative rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-600',
        'flex items-center justify-center font-semibold text-white',
        sizes[size],
        className
      )}
      {...props}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};

// ============================================================================
// SPINNER
// ============================================================================

export const Spinner = ({ size = 'md', className }) => {
  const sizes = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3',
    xl: 'w-12 h-12 border-4'
  };

  return (
    <div
      className={cn(
        'border-purple-500 border-t-transparent rounded-full animate-spin',
        sizes[size],
        className
      )}
    />
  );
};

// ============================================================================
// SKELETON
// ============================================================================

export const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        'bg-zinc-800/50 rounded-lg animate-pulse',
        className
      )}
      {...props}
    />
  );
};

// ============================================================================
// DIVIDER
// ============================================================================

export const Divider = ({ className, ...props }) => {
  return (
    <div
      className={cn('h-px bg-zinc-800/50', className)}
      {...props}
    />
  );
};

// ============================================================================
// TOOLTIP (Simple version)
// ============================================================================

export const Tooltip = ({ children, content, position = 'top' }) => {
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  return (
    <div className="relative group">
      {children}
      <div
        className={cn(
          'absolute z-50 px-2 py-1 text-xs font-medium text-white bg-zinc-800 rounded-lg',
          'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
          'transition-all duration-200 whitespace-nowrap',
          'shadow-lg shadow-black/20',
          positions[position]
        )}
      >
        {content}
      </div>
    </div>
  );
};
